<script setup lang="ts">
    import type { StoryblokRichtext } from '~/.storyblok/types/storyblok';
    import {
        CONFIRMATION_KIND,
        FormSubmitStatus,
        FRIENDLYCAPTCHA_ERROR,
        FRIENDLYCAPTCHA_KIND,
        getFinisherOutput,
        frontendFormDefinitionSchema,
        mapFrontendFormDefinition,
        REDIRECT_KIND,
        validateFrontendForm
    } from '@hmmh/storyblok-forms/client';
    import type {
        FormPresentationNode,
        FrontendFormDefinition,
        RichTextDocument,
        SubmitResult
    } from '@hmmh/storyblok-forms/client';
    import type { StoryPage } from '~/types/storyPage';
    import type { FormInputs } from '~/utils/formUtils';
    import { useConditionalFormState } from '~/composables/useConditionalFormState';
    import { formFieldComponentResolver } from './formFieldComponentResolver';
    import FormPresentation from './FormPresentation.vue';

    const props = defineProps<{
        blok: {
            _uid: string;
            component: 'form-definition';
            alignment?: 'left' | 'center' | 'right' | '';
            fields?: unknown[];
            labelPosition?: 'inside' | 'outside' | '';
            _editable?: string;
        };
    }>();

    defineExpose({
        reset: () => {
            reset();
            submitResult.value = undefined;
            submitError.value = undefined;
        }
    });

    provide('formControlId', useId());

    const FORM_REQUEST_TIMEOUT = 10_000;
    const { t } = useI18n();

    const storyPage = useState<StoryPage>('story-page');

    const { data: formDefinition, error } = await useFetch('/api/sb-form-definition', {
        key: `form-definition-${props.blok._uid}`,
        timeout: FORM_REQUEST_TIMEOUT,
        transform: (response: unknown) => frontendFormDefinitionSchema.parse(response),
        query: {
            pageId: storyPage.value?.uuid,
            formId: props.blok._uid
        }
    });

    const submitResult = ref<SubmitResult>();
    const submitError = ref<string>();
    const isSubmitting = ref(false);
    const { beginAttempt, completeAttempt } = useMediceFormSubmissionAttempt();

    const previewFormDefinition = computed<FrontendFormDefinition | undefined>(() => {
        if (!props.blok._editable || !Array.isArray(props.blok.fields)) return undefined;

        try {
            return mapFrontendFormDefinition(props.blok);
        } catch {
            return undefined;
        }
    });

    const renderedFormDefinition = computed(() =>
        props.blok._editable ? previewFormDefinition.value : formDefinition.value
    );

    const {
        formInputs,
        activeFields,
        activeValues,
        projectionError,
        reset,
        clearValidationErrors,
        validationErrorCount,
        toRawData
    } = useConditionalFormState(renderedFormDefinition);

    provide<Ref<FormInputs>>('formInputs', formInputs);
    provide('formActiveValues', activeValues);

    const alignmentClass = computed(() =>
        props.blok.alignment && props.blok.alignment !== 'left' ? `--align-${props.blok.alignment}` : undefined
    );
    const hasRequiredFields = computed(
        () =>
            activeFields.value.some((field) =>
                field.validators?.some(
                    (validator) => validator.kind === 'form_validator_required' || validator.kind === 'required'
                )
            ) ?? false
    );

    const hasIncompleteCaptcha = computed(
        () =>
            activeFields.value.some(
                (field) => field.type === FRIENDLYCAPTCHA_KIND && !formInputs.value[field.name]?.value
            ) ?? false
    );

    const presentation = computed<FormPresentationNode[]>(() => {
        const definition = renderedFormDefinition.value;
        if (!definition) return [];
        return definition.presentation ?? definition.fields.map((field) => ({ kind: 'field', name: field.name }));
    });

    const applyClientValidationErrors = (rawData: Record<string, string>) => {
        const definition = renderedFormDefinition.value;
        if (!definition) return false;

        try {
            const errors = validateFrontendForm(rawData, definition);

            for (const field of activeFields.value) {
                if (field.type === FRIENDLYCAPTCHA_KIND && !formInputs.value[field.name]?.value) {
                    errors[field.name] = [FRIENDLYCAPTCHA_ERROR];
                }
            }
            for (const [fieldId, fieldErrors] of Object.entries(errors)) {
                const input = formInputs.value[fieldId];
                if (input) input.errors = fieldErrors;
            }
            return Object.keys(errors).length === 0;
        } catch {
            submitError.value = t('messages.error');
            return false;
        }
    };

    const resetCaptchaResponses = () => {
        for (const field of activeFields.value) {
            if (field.type === FRIENDLYCAPTCHA_KIND) formInputs.value[field.name]!.value = '';
        }
    };

    const applyValidationErrors = (result: SubmitResult) => {
        if (result.status !== FormSubmitStatus.INVALID_DATA) return;

        for (const [fieldId, errors] of Object.entries(result.errors)) {
            const input = formInputs.value[fieldId];

            if (input && activeFields.value.some((field) => field.name === fieldId)) input.errors = errors;
        }
    };

    const confirmationOutput = computed(() => {
        if (submitResult.value?.status !== FormSubmitStatus.SUCCESS) return undefined;
        return getFinisherOutput<{ content: RichTextDocument }>(submitResult.value.finishers, CONFIRMATION_KIND);
    });

    const confirmationContent = computed(() => confirmationOutput.value?.content);
    const asStoryblokRichtext = (document: RichTextDocument): StoryblokRichtext =>
        document as unknown as StoryblokRichtext;

    // maps a technical (non-user-correctable) submit status to a user-facing
    // message so failures are never silent; SUCCESS and INVALID_DATA are
    // handled by their own branches and return undefined here
    const technicalError = computed(() => {
        const result = submitResult.value;
        if (!result || result.status === FormSubmitStatus.SUCCESS || result.status === FormSubmitStatus.INVALID_DATA) {
            return undefined;
        }

        return t('messages.error');
    });

    const submitForm = async () => {
        const pageId = storyPage.value?.uuid;
        if (!pageId || projectionError.value) {
            submitError.value = t('messages.error');
            return;
        }

        submitError.value = undefined;
        submitResult.value = undefined;
        clearValidationErrors();

        const rawData = toRawData();
        if (!applyClientValidationErrors(rawData)) return;

        isSubmitting.value = true;

        try {
            // `.raw`, because whether this attempt is over is reported in a
            // response header — see `MEDICE_FORM_SUBMISSION_ATTEMPT_HEADER`.
            const response = await $fetch.raw<SubmitResult>('/api/sb-forms', {
                method: 'POST',
                timeout: FORM_REQUEST_TIMEOUT,
                body: {
                    pageId,
                    formId: props.blok._uid,
                    data: rawData,
                    idempotencyKey: beginAttempt(),
                    submissionUrl: import.meta.client ? window.location.href : undefined
                }
            });

            const result = response._data;
            // An answer without a body says nothing about what happened upstream,
            // so it is handled as a lost response: the attempt stays open.
            if (!result) throw new Error('the submit endpoint returned no result');

            // The attempt ends when the submission succeeded, or when the server
            // reports that nothing was stored. Any other failure leaves the
            // outcome unknown — a Salesforce timeout may still have been stored —
            // and the key survives so the retry is recognisable as one.
            if (
                result.status === FormSubmitStatus.SUCCESS ||
                response.headers.get(MEDICE_FORM_SUBMISSION_ATTEMPT_HEADER) === MediceFormSubmissionAttemptState.Restart
            ) {
                completeAttempt();
            }

            submitResult.value = result;
            applyValidationErrors(submitResult.value);

            if (
                submitResult.value.status === FormSubmitStatus.SUCCESS ||
                submitResult.value.status === FormSubmitStatus.UPSTREAM_ERROR
            ) {
                resetCaptchaResponses();
            }

            if (submitResult.value.status === FormSubmitStatus.SUCCESS) {
                const redirect = submitResult.value.finishers.find((finisher) => finisher.kind === REDIRECT_KIND);
                const url = redirect?.output?.url;
                const delaySeconds = redirect?.output?.delaySeconds;
                if (typeof url === 'string') {
                    const redirectUrl = useStoryblokUrl(url);
                    if (typeof delaySeconds === 'number' && Number.isFinite(delaySeconds) && delaySeconds > 0) {
                        await new Promise<void>((resolve) => setTimeout(resolve, delaySeconds * 1000));
                    }
                    await navigateTo(redirectUrl, { external: /^https?:\/\//.test(redirectUrl) });
                }
            }
        } catch {
            submitError.value = t('messages.error');
        } finally {
            isSubmitting.value = false;
        }
    };
</script>

<template>
    <div
        v-editable="props.blok"
        class="form-definition form-wrapper"
        :class="{ '--labels-outside': props.blok.labelPosition === 'outside' }"
    >
        <p
            v-if="error || projectionError || (props.blok._editable && !renderedFormDefinition)"
            role="alert"
        >
            {{ t('messages.error') }}
        </p>

        <template v-else-if="renderedFormDefinition && !confirmationOutput">
            <form
                class="input-wrapper"
                novalidate
                @submit.prevent="submitForm"
            >
                <FormPresentation
                    :nodes="presentation"
                    :fields="activeFields"
                    :field-component-resolver="formFieldComponentResolver"
                    :label-position="blok.labelPosition"
                />

                <p
                    v-if="hasRequiredFields"
                    class="required-message"
                    :class="alignmentClass"
                >
                    <span>*</span> {{ t('formElement.required') }}
                </p>

                <div
                    class="submit-btn-wrapper"
                    :class="alignmentClass"
                >
                    <PrimaryButton
                        :button-text="renderedFormDefinition.submitLabel || t('form.submit')"
                        type="submit"
                        :native-button="true"
                        size="medium"
                        :is-pending="isSubmitting"
                        :disabled="hasIncompleteCaptcha"
                    />
                </div>
            </form>
        </template>

        <p
            v-if="submitError"
            class="error"
            role="alert"
        >
            {{ submitError }}
        </p>
        <p
            v-else-if="validationErrorCount > 0"
            class="error"
            role="alert"
        >
            {{ t('formElement.errorCountMessage', { count: validationErrorCount }) }}
        </p>
        <p
            v-else-if="technicalError"
            class="error"
            role="alert"
        >
            {{ technicalError }}
        </p>
        <div
            v-else-if="submitResult?.status === FormSubmitStatus.SUCCESS"
            class="success"
            role="status"
        >
            <RichText
                v-if="confirmationContent"
                :doc="asStoryblokRichtext(confirmationContent)"
            />
            <template v-else>{{ t('formElement.formSubmissionSuccess') }}</template>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/form/form-group.scss';
    @import '~/assets/scss/form/form-errors.scss';

    .form-definition :deep(select:hover) {
        filter: brightness(0.98);
    }

    .form-definition :deep(select:focus-visible) {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }

    .form-definition :deep(select:active),
    .form-definition :deep(input[type='checkbox']:active) {
        filter: brightness(0.92);
    }

    @media (prefers-reduced-motion: reduce) {
        .form-definition :deep(*),
        .form-definition :deep(*::before),
        .form-definition :deep(*::after) {
            animation: none !important;
            transition: none !important;
        }
    }
</style>
